import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { Chip } from "@components/ui/Chip"
import { Icon } from "@components/ui/Icon"
import { WishlistIcon } from "@features/wishlist/WishlistIcon"
import { formatDateTime } from "@utils/date-format"
import { tv } from "@utils/tw"
import { Link } from "eddev/routing"
import { useMemo } from "react"
import { FilmSessionFragment, FilmTileFragment } from "@generated-types"
import { EventBadges } from "./EventBadges"
import { FadeScroller } from "@components/atoms/FadeScroller"
import { trackEvent } from "@features/tracking/tracking"
import { TrackingHelpers } from "@features/tracking/tracking-helpers"
import { trackFilmInterest } from "@features/tracking/film-history"

export type FilmTileProps = {
  previewing?: boolean
  film: FilmTileFragment
  session?: FilmSessionFragment
  sessions?: (FilmSessionFragment | null)[]
  /** Set to enable the preview button */
  onPreview?: () => void
  /** Stops rendering of images and complex UI elements, to improve performance when not in view */
  unloaded?: boolean
  allowMobile?: boolean
  /** Displays a large number, used for 'Top 10 Films' */
  number?: number
}

export function FilmTile(props: FilmTileProps) {
  const film = props.film
  const session = props.session

  const styles = createStyles({ previewing: props.previewing, allowMobile: props.allowMobile })

  const venue = props.session?.info?.venue?.titleSimple
  const date = props.session?.info?.dateTime

  const sessionTags = useMemo(() => {
    const tags: string[] = []
    if (date) {
      tags.push(formatDateTime(date))
    }
    if (venue) {
      tags.push(venue)
    }
    return tags
  }, [venue, date])

  const filmTypeLabel = film.eventInfo?.filmTypeLabel !== "Feature" ? film.eventInfo?.filmTypeLabel : null

  return (
    <Link
      href={film.uri}
      className={styles.base()}
      title={film.title!}
      {...TrackingHelpers.filmAttrs(film)}
      {...TrackingHelpers.sessionAttrs(session)}
      data-mixpanel-component="Film Tile"
      linkData={{ modal: true }}
    >
      {/* Image */}
      <div className={styles.image()}>
        {!props.unloaded && (
          <>
            <ResponsiveImage {...film.media?.thumb!} max />
            {/* Event badges */}
            <div className={styles.imageBadges()}>
              <EventBadges film={props.film} session={props.session} />
            </div>

            {/* Wishlist button */}
            <div className="absolute top-2 right-2 text-white">
              <WishlistIcon film={props.session?.info?.ferveId ?? props.film} />
            </div>

            {/* Session info */}
            <div className={styles.imageSessionBadges()}>
              {filmTypeLabel && (
                <Chip variant="high" shape="rounded">
                  {filmTypeLabel}
                </Chip>
              )}
              {sessionTags.map((tag, i) => (
                <Chip variant="high" shape="rounded" key={i}>
                  {tag}
                </Chip>
              ))}
            </div>
          </>
        )}
      </div>

      <div className={styles.numberWrap()}>
        {/* Big number */}
        {props.number && <div className={styles.number()}>{props.number}</div>}

        {/* Info */}
        <div className={styles.info()}>
          {/* Film title */}
          <div className={styles.title()}>{film.title}</div>
          {film.excerpt && (
            <div className={styles.excerpt()}>
              <span dangerouslySetInnerHTML={{ __html: film.descriptions?.short! }} />
            </div>
          )}
          {props.allowMobile && !props.unloaded && (
            <div className={styles.mobileBadges()}>
              <FadeScroller className="overscroll-auto">
                <div className="flex flex-auto gap-2">
                  <EventBadges film={props.film} session={props.session} />
                  {sessionTags.map((tag, i) => (
                    <Chip variant="high" shape="rounded" key={i}>
                      {tag}
                    </Chip>
                  ))}
                </div>
              </FadeScroller>
            </div>
          )}
        </div>
      </div>

      <div className={styles.buttonContainer()}>
        {props.onPreview && (
          <button
            className={buttonStyles({ kind: "preview" })}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              props.onPreview!()
              trackEvent("Open Preview", {}, e.currentTarget)
              trackFilmInterest(film.uri!, "preview")
            }}
          >
            <span data-part="icon" />
            Preview
            <Icon data-part="icon" name="chevronDown" />
          </button>
        )}
        <button className={buttonStyles({ kind: "moreInfo" })}>More info</button>
      </div>
    </Link>
  )
}

const createStyles = tv({
  base: "group flex flex-col relative isolate h-full hover-card",
  slots: {
    image: "relative overflow-hidden md:group-hover:rounded-md transition-[border-radius] duration-150 aspect-[16/9]",
    imageBadges: "absolute top-2 left-2 flex gap-2",
    imageSessionBadges: "absolute left-2 bottom-2 flex flex-wrap gap-2",
    meta: "absolute bottom-3 left-3 gap-2 md:bottom-2 md:left-2 md:gap-1 flex pointer-events-none",
    info: "flex flex-col",
    title: "type-label-m mb-2 text-fg",
    excerpt: "type-body-s line-clamp-3 text-fg-secondary",
    buttonContainer: [
      "mt-4 hidden md:flex flex-grow justify-stretch items-end opacity-0 group-hover:opacity-100 transition-opacity duration-150",
      "gap-[--border-radius-sm] [&>*]:flex-1",
    ],
    mobileBadges: "hidden",
    numberWrap: "flex items-start mt-3",
    number: "type-title-xxl xl:type-title-xl !leading-[0.8em] mr-[0.3em] text-fg",
  },
  variants: {
    allowMobile: {
      true: {
        base: "max-sm:flex-row max-sm:gap-grid-gutter max-sm:w-grid",
        info: "max-sm:w-cols-8",
        image: "max-sm:aspect-[12/9] max-sm:w-cols-4 max-sm:flex-none rounded-sm md:rounded-none",
        title: "max-sm:mt-0 max-sm:mb-3",
        excerpt: "max-sm:line-clamp-2",
        imageBadges: "max-sm:hidden",
        imageSessionBadges: "max-sm:hidden",
        mobileBadges: "max-sm:flex flex-1 items-end min-h-5 mt-3",
      },
    },
    previewing: {
      true: {
        base: "hover-card-previewing",
        buttonContainer: "!opacity-0 pointer-events-none",
      },
    },
  },
  defaultVariants: {},
})

const buttonStyles = tv({
  base: [
    "inline-flex items-center justify-center gap-[0em] whitespace-nowrap type-body-s",
    "border border-current rounded-md px-3 h-[2.7em]",
    "transition-colors",
    "part-[icon]:size-[2em]",
  ],
  variants: {
    kind: {
      preview: "bg-transparent text-fg border border-bg-mid hover:bg-bg-mid/50 active:bg-bg-mid",
      moreInfo: "bg-fg text-bg hover:bg-fg/90",
    },
  },
})
